// Angular + external
import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewEncapsulation,
} from "@angular/core";

// BFX
import * as CubiqTS from "shared/cubiq-model-multi-asset";
import { BfxBaseBlotter, provideBlotter } from "../bfx-base-blotter/bfx-base-blotter";

import { TranslateService } from "@ngx-translate/core";
import { GoldenLayoutService } from "../golden-layout/golden-layout-service";

import { ProfilePromiseActionService, SystemConfigPromiseService } from "shared/cubiq-services";
import { BfxBaseBlotterColumnSelectorService } from "../bfx-base-blotter/bfx-base-blotter-columnselector.service";
import { BfxBlotterFilterService } from "../bfx-blotter-filter/bfx-blotter-filter.service";

import { BfxMouseModifierKeyService } from "app/bfx-common/services/bfx-mouse-modifier-key.service";
import { BfxUserProfileService } from "../bfx-common/services/bfx-user-profile.service";

import { GridOptions, RowNode } from "ag-grid-community";
import {
  BfxAllocationBlotterService,
  OrderAllocationContainer,
} from "app/bfx-order-details/services/bfx-allocations.service";
import { AllocationTableDefinitionFactory } from "shared/cubiq-services/model/AllocationTableDefinitionFactory";
import { BfxSaveProfileNotificationService } from "../bfx-common/services/bfx-save-profile-notification.service";

import { LoggerService } from "@bfx/logger";
import { CommonIntentRenderer } from "app/bfx-blotter-orders/renderers/CommonIntentRenderer";
const logger = LoggerService.for(__dirname);

/**
 * Blotter for AllocationInfoDetails objects - theese are the output of the DEtailedOrderStatus request service and not
 * raw allocation models. The raw data need for sorting is in the rawValues property of the allocation
 */
@Component({
  selector: "bfx-allocations-blotter",
  templateUrl: "./html/bfx-allocations-blotter.html",
  encapsulation: ViewEncapsulation.None,
  providers: [provideBlotter(BfxAllocationsBlotterComponent), BfxAllocationBlotterService],
  viewProviders: [BfxBaseBlotterColumnSelectorService],
  standalone: false,
})
export class BfxAllocationsBlotterComponent extends BfxBaseBlotter implements OnChanges {
  @Input()
  public searchEnabled = false;

  public frameworkComponents: any = {
    intentRenderer: CommonIntentRenderer,
  };

  /** Container for for allocation info */
  @Input() order: OrderAllocationContainer;

  /** Ccy pair used for filtering allocations */
  @Input() ccyPair: string;

  /** Emit a deal Id or null when row selection is made */
  @Output() selectedDealId: EventEmitter<string> = new EventEmitter();

  constructor(
    BfxBlotterElement: ElementRef,
    public service: BfxAllocationBlotterService,
    tableDefinitionFactory: AllocationTableDefinitionFactory,
    layoutService: GoldenLayoutService,
    translationService: TranslateService,
    blotterFilterService: BfxBlotterFilterService,
    columnSelectorService: BfxBaseBlotterColumnSelectorService,
    profileActionService: ProfilePromiseActionService,
    sysConfigService: SystemConfigPromiseService,
    mouseModifierKeyService: BfxMouseModifierKeyService,
    saveProfileNotificationService: BfxSaveProfileNotificationService,
    userProfileService: BfxUserProfileService,
  ) {
    super(
      BfxBlotterElement,
      null,
      null,
      service,
      null,
      null,
      tableDefinitionFactory,
      layoutService,
      translationService,
      blotterFilterService,
      null,
      columnSelectorService,
      profileActionService,
      sysConfigService,
      mouseModifierKeyService,
      saveProfileNotificationService,
      userProfileService,
    );
  }

  protected createsGridOptions(): GridOptions {
    let go = super.createsGridOptions();
    go.onRowSelected = (event) => {
      // Add row selection handler so we can determine if we have single selected trade
      this.onRowSelected(event);
    };

    return go;
  }

  /**
   *
   * @param changes
   */
  ngOnChanges(changes: SimpleChanges): void {
    for (let propName in changes) {
      switch (propName) {
        case "order":
          // If order has changed, then we update the service
          this.updateOrderModel();
          break;
        case "ccyPair":
          // Where pair has changed, we update the filter
          if (this.order) {
            if (this.gridOptions?.api) {
              this.gridOptions.api.onFilterChanged();
              this.afterFilteringCompleted();
            }
          }
          break;
      }
    }
  }

  public override getBlotterType(): CubiqTS.BlotterType {
    return CubiqTS.BlotterType.ALLOCATION;
  }

  public getTabIdentifier(): string {
    return "BfxAllocationBlotterComponent";
  }

  public override isExternalFilterPresent() {
    return this.ccyPair != null && this.ccyPair != "";
  }

  public doesExternalFilterPass(node: RowNode): any {
    if (node.data.instrument === this.ccyPair) {
      return true;
    }
    return false;
  }

  private updateOrderModel() {
    this.service.setOrder(this.order);
  }

  /**
   * Emit a deal Id on row selection - only if single row selected otherwise dealId always emitted as null
   * @param event
   */
  public override onRowSelected(event: any): void {
    const selectedAllocations = this.gridOptions.api.getSelectedRows();
    if (selectedAllocations.length === 1) {
      this.selectedDealId.emit(selectedAllocations[0]?.dealId ?? null);
    } else {
      this.selectedDealId.emit(null);
    }
  }

  /**
   * Override so all Allocation Blotters get the same ID
   * @returns
   */
  protected override getBlotterVersion(): string {
    return this._tableDefinitionFactory.getVersionIdentifier();
  }

  /**
   * Restore configuration and refresh columns if necessary
   * @param config
   */
  public restoreConfiguration(config: any) {
    this.restoreFromConfiguration(config);
    if (this.tableDefinition) {
      // We've already create the definition so need to rerun
      this.initializeColumnDefinitions();
    }
  }
}
